import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PROJECTS } from "@/data/mock";

export default function ProjectDetail() {
  const { id } = useParams();
  const project = useMemo(() => PROJECTS.find((p) => p.id === id), [id]);

  if (!project) return <Navigate to="/projects" replace />;

  // All photos for this job: main image + any gallery extras
  const photos = [project.image, ...(project.gallery || [])].filter(Boolean);

  return (
    <div data-testid="project-detail-page" className="bg-brand-bgOchreSoft">
      {/* Back link */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10">
        <Link
          to="/projects"
          data-testid="project-detail-back"
          className="inline-flex items-center gap-2 text-sm text-brand-inkSoft hover:text-brand-terracotta"
        >
          <ArrowLeft className="w-4 h-4" /> All projects
        </Link>
      </div>

      {/* Title */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-8 md:pt-12 pb-10 md:pb-14">
        <h1
          data-testid="project-detail-title"
          className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink text-balance max-w-4xl"
        >
          {project.title}
        </h1>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
          data-testid="project-detail-gallery"
        >
          {photos.map((src, i) => (
            <figure
              key={i}
              className={`overflow-hidden rounded-3xl border border-brand-line bg-white ${
                i === 0 ? "md:col-span-2" : ""
              }`}
            >
              <img
                src={src}
                alt={`${project.title} — photo ${i + 1}`}
                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
              />
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-ink text-brand-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight max-w-xl">
            Like the look of this? Let’s talk about yours.
          </h2>
          <Button
            asChild
            data-testid="project-detail-cta-bottom"
            className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white self-start md:self-auto"
          >
            <Link to={`/contact?service=${project.category}`}>
              Request a quote <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
